import { diffDays, parseDate, startOfToday } from '../../../utils/monthRef';
import { getProdCoordenadorPfId, getRegs } from '../../../utils/fc';
import {
  isAprovado,
  isAguardandoAprovacao,
  PROD_DASHBOARD_FIELDS,
  prodStatusLabel,
  statusId,
} from '../Products/dashboardProdutosHelpers';

export { prodStatusLabel, statusId };

export const DASHBOARD_ENTREGAS_FIELDS = {
  ...PROD_DASHBOARD_FIELDS,
  prod_dtInterna: 'prod_dtInterna',
  '(SELECT proj.projSituacao_id FROM proj WHERE proj.proj_id = prod.proj_id)': 'projSituacao_id',
};

/**
 * Prazos exibidos em todo card do Kanban (ícone + data).
 * doneField / isDone usam os marcos já carregados neste dashboard.
 */
export const CARD_DEADLINE_ROWS = [
  {
    key: 'entrega',
    field: 'prod_dtInterna',
    label: 'Entrega',
    doneField: 'prodEntregueAoClienteData',
  },
  {
    key: 'aprovacao',
    field: 'prod_dtAprov',
    label: 'Aprovação',
    doneField: 'prodAprovacaoData',
  },
  {
    key: 'faturar',
    field: 'prod_dtLimAprov',
    label: 'Faturar',
    doneField: 'prodAutFatData', // fallback prod_dtAutFat em getStageDoneDate
  },
];

/** Cores de texto/ícone das etapas no card. */
export const STAGE_UI_COLORS = {
  black: '#212529',
  green: '#1cbb8c',
  red: '#ff5757',
};

/** Pessoas com visão completa (IDs estáveis no Oikos). */
export const DASHBOARD_ENTREGAS_FULL_VIEW_PF_IDS = [1, 2, 3, 5, 31, 104];

const FULL_VIEW_NAME_NEEDLES = [
  'juliano moreira',
  'ronaldo godolphim',
  'bernardo alcalde',
  'gustavo leite',
  'juliano britto rocha',
  'juliano rocha',
  'jean antonio',
];

const FULL_VIEW_FIRST_NAMES = ['nicolas', 'monila'];

const FULL_VIEW_CARGOS = ['diretor tecnico', 'gerente tecnico'];

export const VIEW_KEYS = {
  aEntregar: 'a_entregar',
  aAprovar: 'a_aprovar',
  aLiberar: 'a_liberar',
  entregaAtraso: 'entrega_atraso',
  fatAtraso: 'fat_atraso',
};

export const VIEW_TITLES = {
  [VIEW_KEYS.aEntregar]: 'Próximos produtos a serem entregues',
  [VIEW_KEYS.aAprovar]: 'Próximos produtos a serem aprovados',
  [VIEW_KEYS.aLiberar]: 'Próximos produtos a serem liberados para faturamento',
  [VIEW_KEYS.entregaAtraso]: 'Produtos com data de entrega estourada',
  [VIEW_KEYS.fatAtraso]: 'Produtos com data de faturamento estourada',
};

/** Títulos curtos das colunas do Kanban (mesmos dos MetricCards). */
export const KANBAN_COLUMN_TITLES = {
  [VIEW_KEYS.aEntregar]: 'Próximos a entregar',
  [VIEW_KEYS.aLiberar]: 'Próximos a liberar p/ faturamento',
};

/** Colunas visíveis do Kanban (sem coluna intermediária de aprovação). */
export const KANBAN_COLUMNS = [
  VIEW_KEYS.aEntregar,
  VIEW_KEYS.aLiberar,
];

/**
 * Prioridade de coluna exclusiva (mais upstream primeiro).
 * Um produto entra em no máximo uma das fatias do Kanban.
 * Após entrega, o fluxo vai direto para liberar p/ faturamento (sem coluna “a aprovar”).
 */
export const KANBAN_EXCLUSIVITY_ORDER = [
  VIEW_KEYS.aEntregar,
  VIEW_KEYS.aLiberar,
];

export function normalizePersonText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export function isFullViewUser(user) {
  if (!user) return false;
  if (Number(user.perm_id) === 1) return true;
  if (DASHBOARD_ENTREGAS_FULL_VIEW_PF_IDS.includes(Number(user.pf_id))) return true;

  const cargo = normalizePersonText(user.pf_cargo);
  if (FULL_VIEW_CARGOS.some((needle) => cargo.includes(needle))) return true;

  const nome = normalizePersonText(user.pf_nomeExibicao || user.pf_nome);
  if (FULL_VIEW_NAME_NEEDLES.some((needle) => nome.includes(needle))) return true;

  const first = nome.split(' ')[0];
  if (FULL_VIEW_FIRST_NAMES.includes(first)) return true;

  return false;
}

export function isEquipeCoordencaoAtiva(row, today = startOfToday()) {
  if (!row) return false;
  if (Number(row.projAtuacao_id) !== 8) return false;
  if (row.projEquipe_ativo != null && Number(row.projEquipe_ativo) !== 1) return false;
  const fim = parseDate(row.projEquipe_fim);
  return !fim || fim >= today;
}

export async function fetchCoordinatedProjIds(pfId) {
  if (!pfId) return [];
  const rows = await getRegs('projEquipe', { pf_id: pfId, projAtuacao_id: 8 });
  const today = startOfToday();
  return [...new Set(
    (Array.isArray(rows) ? rows : [])
      .filter((row) => isEquipeCoordencaoAtiva(row, today))
      .map((row) => Number(row.proj_id))
      .filter(Boolean),
  )];
}

export function resolveEntregasAccess(user, coordinatedProjIds = []) {
  const seeAll = isFullViewUser(user);
  const coordIds = (coordinatedProjIds || []).map(Number).filter(Boolean);
  const isCoordinator = coordIds.length > 0;
  return {
    seeAll,
    isCoordinator,
    canAccess: seeAll || isCoordinator,
    coordinatedProjIds: coordIds,
  };
}

export function filterProductsForAccess(products, user, access) {
  const list = Array.isArray(products) ? products : [];
  if (!access || access.seeAll) return list;
  const allowed = new Set((access.coordinatedProjIds || []).map(Number));
  const pfId = Number(user?.pf_id);
  return list.filter((p) => {
    if (allowed.has(Number(p.proj_id))) return true;
    return Number(getProdCoordenadorPfId(p)) === pfId;
  });
}

export function isProjetoEmAndamento(produto) {
  const sit = Number(produto?.projSituacao_id);
  return !sit || sit === 2;
}

export function hasEntregaCliente(produto) {
  const id = statusId(produto);
  if (id === 6 || isAprovado(id)) return true;
  return Boolean(parseDate(produto?.prodEntregueAoClienteData));
}

export function hasAprovacaoCliente(produto) {
  const id = statusId(produto);
  if (isAprovado(id)) return true;
  return Boolean(parseDate(produto?.prodAprovacaoData));
}

export function hasAutFat(produto) {
  const id = statusId(produto);
  if (id === 8) return true;
  return Boolean(parseDate(produto?.prodAutFatData) || parseDate(produto?.prod_dtAutFat));
}

/** Etapa cumprida (mesmos predicados das fatias / status). */
export function isStageFulfilled(produto, stageKey) {
  if (stageKey === 'entrega') return hasEntregaCliente(produto);
  if (stageKey === 'aprovacao') return hasAprovacaoCliente(produto);
  if (stageKey === 'faturar') return hasAutFat(produto);
  return false;
}

/** Data real de cumprimento da etapa (quando existir no registro). */
export function getStageDoneDate(produto, stageKey) {
  if (stageKey === 'entrega') return parseDate(produto?.prodEntregueAoClienteData);
  if (stageKey === 'aprovacao') return parseDate(produto?.prodAprovacaoData);
  if (stageKey === 'faturar') {
    return parseDate(produto?.prodAutFatData) || parseDate(produto?.prod_dtAutFat);
  }
  return null;
}

/**
 * Cores por etapa no card.
 * Texto: vermelho = vencida ou cumprida com atraso; verde = cumprida no prazo; preto = prazo ainda não chegou.
 * Ícone: verde = cumprida; vermelho = em atraso e não cumprida; preto = em dia e não cumprida.
 */
export function getStageUiColors(produto, stageKey, today = startOfToday()) {
  const row = CARD_DEADLINE_ROWS.find((r) => r.key === stageKey);
  const deadline = parseDate(produto?.[row?.field]);
  const fulfilled = isStageFulfilled(produto, stageKey);
  const doneDate = getStageDoneDate(produto, stageKey);
  const { black, green, red } = STAGE_UI_COLORS;

  if (!deadline) {
    return {
      dateColor: black,
      iconColor: fulfilled ? green : black,
      fulfilled,
      late: false,
      overduePending: false,
    };
  }

  if (fulfilled) {
    // Sem data de realização: alinhado a classifyMarcoBucket → trata como no prazo.
    const late = Boolean(doneDate && doneDate > deadline);
    return {
      dateColor: late ? red : green,
      iconColor: green,
      fulfilled: true,
      late,
      overduePending: false,
    };
  }

  const overduePending = deadline < today;
  return {
    dateColor: overduePending ? red : black,
    iconColor: overduePending ? red : black,
    fulfilled: false,
    late: false,
    overduePending,
  };
}

/** Ainda não entregue ao cliente (status 1–5, 9 ou sem marco de entrega). */
export function isPendenteEntrega(produto) {
  const id = statusId(produto);
  if (id === 9) return true;
  if (isAguardandoAprovacao(id) || isAprovado(id)) return false;
  return !hasEntregaCliente(produto);
}

export function isPendenteAprovacao(produto) {
  if (hasAprovacaoCliente(produto)) return false;
  if (isAguardandoAprovacao(statusId(produto))) return true;
  return hasEntregaCliente(produto) && statusId(produto) !== 9;
}

export function isPendenteLiberacaoFat(produto) {
  return !hasAutFat(produto) && Boolean(parseDate(produto?.prod_dtLimAprov));
}

/**
 * Coluna Kanban exclusiva para o produto, ou null se fora do filtro/janela.
 * Prioridade: a_entregar → a_liberar (nunca duas ao mesmo tempo).
 * Entregue (não pendente de entrega) e ainda sem aut. fat → a_liberar.
 */
export function assignKanbanColumn(produto, dateRange = {}) {
  const dateFrom = dateRange.dateFrom || DASHBOARD_ENTREGAS_DATE_CUTOFF;
  const dateTo = dateRange.dateTo || null;

  if (isPendenteEntrega(produto) && isDateInRange(produto?.prod_dtInterna, dateFrom, dateTo)) {
    return VIEW_KEYS.aEntregar;
  }
  // Pós-entrega: pula “a aprovar”; se ainda falta liberar p/ faturamento, vai para a_liberar.
  if (isPendenteLiberacaoFat(produto) && isDateInRange(produto?.prod_dtLimAprov, dateFrom, dateTo)) {
    return VIEW_KEYS.aLiberar;
  }
  return null;
}

export function sortByDateAsc(list, getDate) {
  return [...list].sort((a, b) => {
    const da = getDate(a)?.getTime() ?? Number.POSITIVE_INFINITY;
    const db = getDate(b)?.getTime() ?? Number.POSITIVE_INFINITY;
    return da - db;
  });
}

export function nearestDate(list, getDate) {
  const dates = list.map(getDate).filter(Boolean).sort((a, b) => a - b);
  return dates[0] || null;
}

export const DASHBOARD_ENTREGAS_DATE_CUTOFF = parseDate('2026-06-01');

export function toDateInputValue(value) {
  const d = parseDate(value);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function isDateInRange(value, dateFrom, dateTo) {
  const d = parseDate(value);
  if (!d) return false;
  const from = parseDate(dateFrom) || DASHBOARD_ENTREGAS_DATE_CUTOFF;
  const to = parseDate(dateTo);
  if (from && d.getTime() < from.getTime()) return false;
  if (to && d.getTime() > to.getTime()) return false;
  return true;
}

export function buildEntregasSlices(products, today = startOfToday(), dateRange = {}) {
  const range = {
    dateFrom: dateRange.dateFrom || DASHBOARD_ENTREGAS_DATE_CUTOFF,
    dateTo: dateRange.dateTo || null,
  };
  const eligible = (Array.isArray(products) ? products : []).filter((p) => (
    p && typeof p === 'object' && Number(p.prod_ativo) !== 0 && isProjetoEmAndamento(p)
  ));

  const aEntregar = [];
  const aLiberar = [];

  // Fatias disjuntas: cada produto em no máximo uma coluna (prioridade upstream).
  for (const p of eligible) {
    const col = assignKanbanColumn(p, range);
    if (col === VIEW_KEYS.aEntregar) aEntregar.push(p);
    else if (col === VIEW_KEYS.aLiberar) aLiberar.push(p);
  }

  const aEntregarSorted = sortByDateAsc(aEntregar, (p) => parseDate(p.prod_dtInterna));
  const aLiberarSorted = sortByDateAsc(aLiberar, (p) => parseDate(p.prod_dtLimAprov));

  const entregaAtraso = sortByDateAsc(
    aEntregarSorted.filter((p) => {
      const d = parseDate(p.prod_dtInterna);
      return d && d < today;
    }),
    (p) => parseDate(p.prod_dtInterna),
  );
  const fatAtraso = sortByDateAsc(
    aLiberarSorted.filter((p) => {
      const d = parseDate(p.prod_dtLimAprov);
      return d && d < today;
    }),
    (p) => parseDate(p.prod_dtLimAprov),
  );

  return {
    aEntregar: aEntregarSorted,
    aAprovar: [], // coluna removida do Kanban; mantido vazio por compatibilidade
    aLiberar: aLiberarSorted,
    entregaAtraso,
    fatAtraso,
  };
}

export function slicesByView(slices, viewKey) {
  const map = {
    [VIEW_KEYS.aEntregar]: slices.aEntregar,
    [VIEW_KEYS.aAprovar]: slices.aAprovar,
    [VIEW_KEYS.aLiberar]: slices.aLiberar,
    [VIEW_KEYS.entregaAtraso]: slices.entregaAtraso,
    [VIEW_KEYS.fatAtraso]: slices.fatAtraso,
  };
  return map[viewKey] || slices.aEntregar;
}

export function dueDateForView(produto, viewKey) {
  if (viewKey === VIEW_KEYS.aEntregar || viewKey === VIEW_KEYS.entregaAtraso) {
    return parseDate(produto?.prod_dtInterna);
  }
  return parseDate(produto?.prod_dtLimAprov);
}

export function daysUntilDue(produto, viewKey, today = startOfToday()) {
  const d = dueDateForView(produto, viewKey);
  if (!d) return null;
  return diffDays(d, today);
}

/**
 * Resumo por coluna: total | vencidas | a vencer em N dias (inclusivo).
 */
export function countDueWindow(
  list,
  viewKey,
  { overdue = true, withinDays = 10 } = {},
  today = startOfToday(),
) {
  const items = Array.isArray(list) ? list : [];
  let overdueCount = 0;
  let withinCount = 0;
  for (const item of items) {
    const d = daysUntilDue(item, viewKey, today);
    if (d == null) continue;
    if (overdue && d < 0) overdueCount += 1;
    else if (d >= 0 && d <= withinDays) withinCount += 1;
  }
  return {
    total: items.length,
    overdue: overdueCount,
    withinDays: withinCount,
  };
}

export function productDisplayName(produto) {
  return produto?.prod_desc || produto?.prod_nome || 'Produto';
}
